/**
 * Ring geometry — signed area, perimeter, orientation, Douglas-Peucker.
 *
 * A *ring* here is a closed polygon stored WITHOUT a repeated closing vertex:
 * `[p0, p1, … pn]` implies the edge `pn → p0`. Every helper below closes the
 * ring itself, so a caller never has to decide whether to duplicate the first
 * point (the single most common source of an off-by-one segment in polygon
 * code).
 *
 * The shoelace and the perimeter exist a second time on the Worker side
 * (`src/worker/lib/geom2d.ts`, where the saya and DXF libraries share one copy)
 * and they must agree to the last bit: the browser measures a traced part here,
 * the Worker re-measures the same rings there and stores ITS numbers. The
 * duplication is structural — the app project compiles nothing out of the
 * Worker tree except `api/*.contract.ts` — so a change to either belongs in
 * both. Simplification and orientation have no Worker twin: the browser owns
 * tracing.
 */

/** `[x, y]`. Pixel-space rings carry integer pixel centres; mm rings carry 0.01 mm values. */
export type Point = [number, number]

/**
 * Twice the signed area of a closed ring (the raw shoelace sum).
 *
 * Sign convention is the mathematical one for the coordinate system the points
 * live in: positive means counter-clockwise when x grows right and y grows *up*.
 * Pixel rasters have y growing *down*, so in pixel space the sign is inverted
 * relative to what you see on screen — see {@link orientRing}.
 */
export function shoelace2(ring: readonly Point[]): number {
  const n = ring.length
  if (n < 3) return 0
  let sum = 0
  for (let i = 0; i < n; i += 1) {
    const a = ring[i]!
    const b = ring[(i + 1) % n]!
    sum += a[0] * b[1] - b[0] * a[1]
  }
  return sum
}

/** Signed area of a closed ring; positive = counter-clockwise in a y-up frame. */
export function signedArea(ring: readonly Point[]): number {
  return shoelace2(ring) / 2
}

/** Absolute area of a closed ring. Degenerate rings (< 3 points) are 0. */
export function ringArea(ring: readonly Point[]): number {
  return Math.abs(signedArea(ring))
}

/** Length of a closed ring, including the implicit `last → first` edge. */
export function ringPerimeter(ring: readonly Point[]): number {
  const n = ring.length
  if (n < 2) return 0
  let total = 0
  for (let i = 0; i < n; i += 1) {
    const a = ring[i]!
    const b = ring[(i + 1) % n]!
    total += Math.hypot(b[0] - a[0], b[1] - a[1])
  }
  return total
}

/**
 * Returns the ring wound so that its signed area has the requested sign.
 *
 * `wantPositive === false` gives a clockwise-in-a-y-up-frame ring. Rings that
 * are degenerate (fewer than 3 points, or exactly zero area) are returned
 * untouched — there is no orientation to fix and reversing them would only make
 * the output non-deterministic.
 *
 * Reversal keeps the first vertex first (`[p0, pn, … p1]`) so the ring's start
 * point stays the topmost-leftmost boundary pixel that tracing chose. That
 * keeps two traces of the same component byte-identical.
 */
export function orientRing(ring: Point[], wantPositive: boolean): Point[] {
  const area = signedArea(ring)
  if (area === 0) return ring
  if (area > 0 === wantPositive) return ring
  const head = ring[0]!
  const rest = ring.slice(1)
  rest.reverse()
  return [head, ...rest]
}

/** Perpendicular distance from `p` to the infinite line through `a` and `b`. */
function perpendicularDistance(p: Point, a: Point, b: Point): number {
  const dx = b[0] - a[0]
  const dy = b[1] - a[1]
  if (dx === 0 && dy === 0) return Math.hypot(p[0] - a[0], p[1] - a[1])
  // |cross| / |ab| — the standard point-line distance, no square roots per term.
  const cross = Math.abs(dx * (a[1] - p[1]) - dy * (a[0] - p[0]))
  return cross / Math.hypot(dx, dy)
}

/**
 * Douglas-Peucker on an OPEN polyline, endpoints always kept.
 *
 * Iterative (explicit stack) rather than recursive: a full-resolution scan
 * boundary is tens of thousands of points and the worst case (a monotone
 * staircase) recurses once per point, which overflows the JS stack long before
 * it runs out of work.
 */
function simplifyPolyline(points: readonly Point[], tolerance: number): Point[] {
  const n = points.length
  if (n < 3) return points.slice()

  const keep = new Uint8Array(n)
  keep[0] = 1
  keep[n - 1] = 1

  // Each stack entry is a [first, last] index span whose interior is undecided.
  const stack: number[] = [0, n - 1]
  while (stack.length > 0) {
    const last = stack.pop()!
    const first = stack.pop()!
    if (last - first < 2) continue

    let farthest = -1
    let maxDistance = tolerance
    const a = points[first]!
    const b = points[last]!
    for (let i = first + 1; i < last; i += 1) {
      const d = perpendicularDistance(points[i]!, a, b)
      if (d > maxDistance) {
        maxDistance = d
        farthest = i
      }
    }

    if (farthest < 0) continue
    keep[farthest] = 1
    stack.push(first, farthest, farthest, last)
  }

  const out: Point[] = []
  for (let i = 0; i < n; i += 1) if (keep[i] === 1) out.push(points[i]!)
  return out
}

/**
 * Douglas-Peucker for a CLOSED ring, tolerance in the ring's own units (px).
 *
 * A ring has no endpoints to anchor, so this splits it at two points that are
 * guaranteed to be genuine extremes — `ring[0]` (tracing starts at the
 * topmost-leftmost boundary pixel, always a real corner) and the ring point
 * farthest from it — simplifies both halves as open polylines, and rejoins
 * them. Anchoring at `ring[0]` alone (the naive "treat the ring as a polyline"
 * shortcut) lets the far side of the ring collapse into a single chord.
 *
 * A ring that would collapse below 3 points is returned unsimplified: an empty
 * or 2-point ring carries no area and would be reported as `invalid_ring`
 * downstream, which is a worse answer than a slightly denser polygon. Callers
 * that want the collapse to be visible still get it from a genuinely degenerate
 * input, since this only refuses to *create* one.
 */
export function simplifyRing(ring: readonly Point[], tolerance: number): Point[] {
  const n = ring.length
  if (n < 4 || !(tolerance > 0)) return ring.slice()

  const start = ring[0]!
  let splitIndex = 0
  let maxDistance = -1
  for (let i = 1; i < n; i += 1) {
    const p = ring[i]!
    const d = (p[0] - start[0]) ** 2 + (p[1] - start[1]) ** 2
    if (d > maxDistance) {
      maxDistance = d
      splitIndex = i
    }
  }
  if (splitIndex === 0) return ring.slice()

  const firstHalf = ring.slice(0, splitIndex + 1)
  const secondHalf = ring.slice(splitIndex).concat([start])

  const a = simplifyPolyline(firstHalf, tolerance)
  const b = simplifyPolyline(secondHalf, tolerance)
  // `a` ends at splitIndex and `b` starts there; `b` ends at the ring start,
  // which the ring closes implicitly. Drop both duplicates.
  const merged = a.concat(b.slice(1, -1))
  return merged.length >= 3 ? merged : ring.slice()
}
